<template>
  <b-modal
    :visible="isVisible"
    :title="$t('order.planning.order-modal-title')"
    hide-footer
    centered
    @hidden="$emit('close')"
  >
    <div v-if="orderData">
      <!-- Order info card -->
      <b-card
        no-body
        class="shadow-none border mb-1 mt-50"
      >
        <div class="p-1">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-25">
            <div class="d-flex align-items-center">
              <router-link
                :to="{ name: 'order-view', params: { id: orderData.orderId } }"
                class="font-medium-3 font-weight-bolder mr-50"
              >
                {{ orderData.fullNumber || orderData.title || '-' }}
              </router-link>
              <div class="d-flex align-items-center">
                <b-avatar
                  v-if="orderData.returnTransport"
                  v-b-tooltip.hover.top="$t('order.label.base-order-info.return-transport')"
                  size="18"
                  variant="info"
                  class="mr-25"
                >
                  <feather-icon
                    icon="RepeatIcon"
                    size="12"
                  />
                </b-avatar>
                <b-avatar
                  v-if="orderData.prioritySignals"
                  :title="$t('order.label.base-order-info.priority-signals')"
                  size="18"
                  variant="primary"
                  class="mr-25"
                >
                  <feather-icon
                    icon="LoaderIcon"
                    size="12"
                  />
                </b-avatar>
                <b-avatar
                  v-if="orderData.absolutePunctuality"
                  :title="$t('order.label.base-order-info.absolute-punctuality')"
                  size="18"
                  variant="danger"
                  class="mr-25"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                  />
                </b-avatar>
                <b-avatar
                  v-if="orderData.infectious"
                  :title="$t('order.label.base-order-info.infectious')"
                  size="18"
                  variant="warning"
                  class="mr-25"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="12"
                  />
                </b-avatar>
                <b-avatar
                  v-if="orderData.private"
                  :title="$t('order.label.base-order-info.private')"
                  size="18"
                  variant="success"
                  class="mr-25"
                >
                  <feather-icon
                    icon="DollarSignIcon"
                    size="12"
                  />
                </b-avatar>
              </div>
            </div>
            <b-badge
              variant="light-primary"
              class="font-small-2 px-75 py-50"
            >
              {{ readableStartAt }}
            </b-badge>
          </div>

          <div
            v-if="orderData.customerName"
            class="d-flex align-items-center text-muted font-small-3 mt-25"
          >
            <feather-icon
              icon="BriefcaseIcon"
              size="13"
              class="mr-25 flex-shrink-0"
            />
            <span class="text-truncate">{{ orderData.customerName }}</span>
          </div>

          <div
            v-if="eventPatients"
            class="d-flex align-items-center text-muted font-small-3 mt-25"
          >
            <feather-icon
              icon="UserIcon"
              size="13"
              class="mr-25 flex-shrink-0"
            />
            <span class="text-truncate">{{ eventPatients }}</span>
          </div>

          <div
            v-if="orderData.pickupAddress || orderData.deliveryAddress"
            class="d-flex align-items-baseline font-small-3 text-muted mt-50"
          >
            <feather-icon
              icon="ArrowUpCircleIcon"
              size="13"
              class="mr-25 flex-shrink-0 text-success"
            />
            <span class="mr-50">{{ formatOrderAddress(orderData.pickupAddress) }}</span>
            <feather-icon
              icon="ArrowDownCircleIcon"
              size="13"
              class="mr-25 flex-shrink-0 text-danger"
            />
            <span>{{ formatOrderAddress(orderData.deliveryAddress) }}</span>
          </div>
        </div>
      </b-card>

      <!-- Form fields -->
      <b-row>
        <b-col cols="12">
          <b-form-group
            :label="$t('order.planning.team-definition')"
            label-for="eventPopupTeamDefinitions"
            class="mb-75"
          >
            <v-select
              id="eventPopupTeamDefinitions"
              v-model="selectedTeamDefinitionIds"
              :options="teamDefinitionOptions"
              :clearable="false"
              :append-to-body="true"
              :calculate-position="calculateSelectPosition"
              label="text"
              :reduce="option => option.value"
              multiple
              :placeholder="$t('order.planning.select-team-definition')"
            >
              <template #no-options>
                {{ firstTeamIncompatibleWarning ? $t(firstTeamIncompatibleWarning) : $t('order.planning.first-available-no-definitions') }}
              </template>
            </v-select>
            <small
              v-if="firstAvailableSelectionError"
              class="text-danger d-block mt-50"
            >
              {{ $t(firstAvailableSelectionError) }}
            </small>
          </b-form-group>
          <b-alert
            :show="selectedTeamDefinitionIds.length > 1 && !firstAvailableSelectionError"
            variant="info"
            class="mb-75 p-1"
          >
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="mr-50"
            />
            {{ $t('order.planning.first-available-info') }}
          </b-alert>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <b-form-group label-for="eventPopupAssignmentOffset">
            <template #label>
              <span class="d-inline-flex align-items-center">
                <span>{{ $t('order.planning.assignment-at') }}</span>
                <feather-icon
                  :id="assignmentHelpTooltipId"
                  v-b-tooltip.hover.top="$t('order.planning.assignment-help')"
                  icon="InfoIcon"
                  size="14"
                  class="text-muted ml-25 cursor-help"
                />
              </span>
            </template>
            <v-select
              id="eventPopupAssignmentOffset"
              :value="assignmentOffsetInput"
              :options="assignmentOffsetOptions"
              :clearable="false"
              :append-to-body="true"
              :calculate-position="calculateSelectPosition"
              taggable
              push-tags
              :create-option="value => value"
              :placeholder="$t('order.planning.duration-placeholder')"
              @input="handleAssignmentOffsetChange"
            />
            <small
              v-if="assignmentOffsetValidationError"
              class="text-danger d-block mt-50"
            >
              {{ $t(assignmentOffsetValidationError) }}
            </small>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <b-form-group label-for="eventPopupDuration">
            <template #label>
              <span class="d-inline-flex align-items-center">
                <span>{{ $t('order.planning.duration-minutes') }}</span>
                <feather-icon
                  :id="durationHelpTooltipId"
                  v-b-tooltip.hover.top="$t('order.planning.duration-help')"
                  icon="InfoIcon"
                  size="14"
                  class="text-muted ml-25 cursor-help"
                />
              </span>
            </template>
            <v-select
              id="eventPopupDuration"
              :value="durationInput"
              :options="durationOptions"
              :clearable="false"
              :append-to-body="true"
              :calculate-position="calculateSelectPosition"
              taggable
              push-tags
              :create-option="value => value"
              :placeholder="$t('order.planning.duration-placeholder')"
              @input="handleDurationChange"
            />
            <small
              v-if="durationValidationError"
              class="text-danger d-block mt-50"
            >
              {{ $t(durationValidationError) }}
            </small>
          </b-form-group>
        </b-col>

      </b-row>

      <!-- Buttons -->
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <b-button
          variant="outline-danger"
          class="mb-50 mb-sm-0"
          @click="$emit('remove-planning')"
        >
          {{ $t('order.planning.remove-action') }}
        </b-button>

        <b-button
          variant="outline-secondary"
          class="mb-50 mb-sm-0"
          @click="$emit('close')"
        >
          {{ $t('shared.button.close') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  defineComponent, computed, ref, watch,
} from '@vue/composition-api'
import {
  BAlert, BAvatar, BBadge, BButton, BCard, BCol, BFormGroup, BModal, BRow, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import {
  formatPlanningMinutes,
  getPlanningAssignmentOffsetOptions,
  normalizePlanningTimeInput,
  PLANNING_TIME_SUGGESTIONS_MINUTES,
} from '@/helpers/orderPlanning'
import {
  resolvePlannerAddressLabel,
  createPlannerPatientsLabel,
  DEFAULT_PLANNED_DURATION_MINUTES,
} from '@/views/orders/Planner/setup'
import teamDefinitionRequests from '@/http/requests/settings/team-definition/team-definition'

export default defineComponent({
  name: 'OrderPlannerEventPopup',
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BModal,
    BRow,
    FeatherIcon,
    vSelect,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    anchor: { type: null, default: null },
    event: { type: Object, default: null },
    isOpen: { type: Boolean, default: false },
    teamDefinitions: { type: Array, default: () => [] },
  },
  emits: ['close', 'remove-planning', 'save-teams', 'change-duration', 'change-assignment-offset', 'edit', 'view'],
  setup(props, { emit }) {
    const assignmentOffsetValidationError = ref('')
    const durationValidationError = ref('')
    const selectedTeamDefinitionIds = ref([])
    const saveTeamsTimeout = ref(null)
    const fetchedTeamDefinitions = ref([])

    const orderData = computed(() => {
      if (!props.event) return null
      const original = props.event.original || {}
      return {
        ...original,
        ...props.event,
        startAt: props.event.startAt || original.startAt,
        customerName: props.event.customerName || original.customerName,
        patients: props.event.patients || original.patients,
        patientInfo: props.event.patientInfo || original.patientInfo,
        pickupAddress: props.event.pickupAddress || original.pickupAddress,
        deliveryAddress: props.event.deliveryAddress || original.deliveryAddress,
        returnTransport: props.event.returnTransport ?? original.returnTransport,
        prioritySignals: props.event.prioritySignals ?? original.prioritySignals,
        absolutePunctuality: props.event.absolutePunctuality ?? original.absolutePunctuality,
        infectious: props.event.infectious ?? original.infectious,
        private: props.event.private ?? original.private,
        fullNumber: props.event.fullNumber || original.fullNumber || props.event.title,
        orderId: props.event.orderId || original.orderId,
      }
    })

    const isVisible = computed(() => props.isOpen && Boolean(props.event))

    const assignmentOffsetValue = computed(() => formatPlanningMinutes(props.event?.original?.bufferBefore || 0))
    const assignmentOffsetInput = computed(() => assignmentOffsetValue.value || null)
    const assignmentOffsetOptions = computed(() => {
      const options = getPlanningAssignmentOffsetOptions(formatPlanningMinutes)
      return assignmentOffsetInput.value && !options.includes(assignmentOffsetInput.value)
        ? options.concat(assignmentOffsetInput.value)
        : options
    })

    const readableStartAt = computed(() => {
      const startAt = orderData.value?.startAt
      if (!startAt) return '-'
      const start = moment.parseZone(startAt)
      const end = props.event?.end ? moment.parseZone(props.event.end) : null
      const startFormatted = start.isValid() ? start.format('DD.MM.YYYY HH:mm') : '-'
      if (!end || !end.isValid()) return startFormatted
      const endFormatted = end.format('DD.MM.YYYY') === start.format('DD.MM.YYYY')
        ? end.format('HH:mm')
        : end.format('DD.MM.YYYY HH:mm')
      return `${startFormatted} - ${endFormatted}`
    })

    const eventPatients = computed(() => createPlannerPatientsLabel(orderData.value))

    const durationValue = computed(() => {
      const minutes = Number(props.event?.original?.durationMinutes || 0)
      return minutes > 0 ? formatPlanningMinutes(minutes) : null
    })
    const durationInput = computed(() => durationValue.value || null)
    const durationOptions = computed(() => {
      const options = PLANNING_TIME_SUGGESTIONS_MINUTES.map(formatPlanningMinutes)
      return durationInput.value && !options.includes(durationInput.value)
        ? options.concat(durationInput.value)
        : options
    })

    const teamDefinitionOptions = computed(() => {
      if (selectedTeamDefinitionIds.value.length === 0) {
        return fetchedTeamDefinitions.value.map(td => ({ value: td.id, text: td.code }))
      }
      const firstId = selectedTeamDefinitionIds.value[0]
      const firstTd = fetchedTeamDefinitions.value.find(td => Number(td.id) === Number(firstId))
      if (!firstTd?.acceptsFirstAvailableOrders) {
        return [{ value: firstTd.id, text: firstTd.code }]
      }
      return fetchedTeamDefinitions.value.filter(td => td.acceptsFirstAvailableOrders).map(td => ({ value: td.id, text: td.code }))
    })

    const firstTeamIncompatibleWarning = computed(() => {
      if (selectedTeamDefinitionIds.value.length !== 1) return ''
      const firstId = selectedTeamDefinitionIds.value[0]
      const firstTd = fetchedTeamDefinitions.value.find(td => Number(td.id) === Number(firstId))
      return firstTd && !firstTd.acceptsFirstAvailableOrders
        ? 'order.planning.first-available-first-team-incompatible'
        : ''
    })

    const firstAvailableSelectionError = computed(() => {
      if (selectedTeamDefinitionIds.value.length <= 1) return ''
      const hasInvalid = selectedTeamDefinitionIds.value.some(id => {
        const td = fetchedTeamDefinitions.value.find(t => Number(t.id) === Number(id))
        return td && !td.acceptsFirstAvailableOrders
      })
      return hasInvalid ? 'order.planning.first-available-team-invalid' : ''
    })

    const initTeamDefinitionIds = () => {
      const original = props.event?.original || {}
      if (original.isFirstAvailablePlanning && Array.isArray(original.plannedTeamDefinitions)) {
        selectedTeamDefinitionIds.value = original.plannedTeamDefinitions.map(td => td.id).filter(Boolean)
      } else {
        const singleId = original.plannedTeamDefinition?.id || null
        selectedTeamDefinitionIds.value = singleId ? [singleId] : []
      }
    }

    const handleSaveTeams = () => {
      const orderId = props.event?.original?.orderId
      if (!orderId || !selectedTeamDefinitionIds.value.length || firstAvailableSelectionError.value) return
      const isFirstAvailable = selectedTeamDefinitionIds.value.length > 1
      const payload = isFirstAvailable
        ? {
          firstAvailable: true,
          teamDefinitions: selectedTeamDefinitionIds.value.map(Number),
          assignmentOffsetMinutes: Number(props.event?.original?.bufferBefore || 0),
          durationMinutes: Number(props.event?.original?.durationMinutes || DEFAULT_PLANNED_DURATION_MINUTES),
        }
        : {
          teamDefinition: Number(selectedTeamDefinitionIds.value[0]),
          assignmentOffsetMinutes: Number(props.event?.original?.bufferBefore || 0),
          durationMinutes: Number(props.event?.original?.durationMinutes || DEFAULT_PLANNED_DURATION_MINUTES),
        }
      emit('save-teams', { orderId, payload })
    }

    watch(isVisible, async value => {
      if (!value) {
        clearTimeout(saveTeamsTimeout.value)
        assignmentOffsetValidationError.value = ''
        durationValidationError.value = ''
        return
      }
      initTeamDefinitionIds()
      try {
        const response = await teamDefinitionRequests.getTeamDefinitions({ limit: 250, sortField: 'code', sortDir: 'asc' })
        const data = response?.data
        let items = []
        if (Array.isArray(data?.items)) {
          items = data.items
        } else if (Array.isArray(data)) {
          items = data
        }
        fetchedTeamDefinitions.value = items
      } catch {
        fetchedTeamDefinitions.value = []
      }
    })

    watch(() => props.event, () => { initTeamDefinitionIds() })

    watch(selectedTeamDefinitionIds, newIds => {
      if (!isVisible.value || !newIds.length) return
      const original = props.event?.original || {}
      let eventIds
      if (original.isFirstAvailablePlanning && Array.isArray(original.plannedTeamDefinitions)) {
        eventIds = original.plannedTeamDefinitions.map(td => Number(td.id)).filter(Boolean)
      } else {
        eventIds = original.plannedTeamDefinition?.id ? [Number(original.plannedTeamDefinition.id)] : []
      }
      const sortedEvent = [...eventIds].sort()
      const sortedNew = [...newIds].map(Number).sort()
      const unchanged = sortedEvent.length === sortedNew.length
        && sortedEvent.every((id, i) => id === sortedNew[i])
      if (unchanged) return
      clearTimeout(saveTeamsTimeout.value)
      saveTeamsTimeout.value = setTimeout(() => { handleSaveTeams() }, 400)
    })

    const handleDurationChange = value => {
      const { parsedValue } = normalizePlanningTimeInput(value)
      if (parsedValue === null || parsedValue <= 0) {
        durationValidationError.value = 'order.planning.duration-invalid'
        return
      }
      durationValidationError.value = ''
      emit('change-duration', parsedValue)
    }

    const handleAssignmentOffsetChange = value => {
      const { parsedValue } = normalizePlanningTimeInput(value)
      if (parsedValue === null) {
        assignmentOffsetValidationError.value = 'order.planning.duration-invalid'
        return
      }
      assignmentOffsetValidationError.value = ''
      emit('change-assignment-offset', parsedValue)
    }

    const calculateSelectPosition = (dropdownList, component, coordinates) => {
      const toggleBounds = component.$refs.toggle.getBoundingClientRect()
      const dropdownHeight = dropdownList.scrollHeight
      const viewportHeight = window.innerHeight
      const spacing = 8
      const availableBelow = viewportHeight - toggleBounds.bottom
      const shouldOpenAbove = availableBelow < Math.min(dropdownHeight, 240)
      const top = shouldOpenAbove
        ? `${window.scrollY + toggleBounds.top - dropdownHeight - spacing}px`
        : coordinates.top
      const dropdownStyle = dropdownList.style
      dropdownStyle.width = coordinates.width
      dropdownStyle.left = coordinates.left
      dropdownStyle.top = top
      dropdownStyle.zIndex = '1060'
    }

    const formatOrderAddress = address => resolvePlannerAddressLabel(address)

    return {
      assignmentOffsetValidationError,
      durationValidationError,
      firstAvailableSelectionError,
      firstTeamIncompatibleWarning,
      selectedTeamDefinitionIds,
      fetchedTeamDefinitions,
      orderData,
      isVisible,
      assignmentOffsetInput,
      assignmentOffsetOptions,
      readableStartAt,
      eventPatients,
      durationInput,
      durationOptions,
      teamDefinitionOptions,
      assignmentHelpTooltipId: 'order-planner-assignment-help',
      durationHelpTooltipId: 'order-planner-duration-help',
      handleDurationChange,
      handleAssignmentOffsetChange,
      calculateSelectPosition,
      formatOrderAddress,
    }
  },
})
</script>
